import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BaseRequestService, ResponseList } from '@outlaw/core';
import { Observable } from 'rxjs';
import { CommunityItem } from '../../interfaces/community-item';
import { Selection } from '../../interfaces';
import { CommunityConfigService } from '../util';

@Injectable({ providedIn: 'root' })
export class SelectionService {
  #postUrl = inject(CommunityConfigService).internalApi.postUrl;
  #http = inject(HttpClient);
  #baseRequestService = inject(BaseRequestService);

  getSelections(userUuid: string): Observable<Selection[]> {
    return this.#http.get<Selection[]>(
      this.#baseRequestService.prepareUrl([this.#postUrl, 'selections']),
      { params: { user_uuid: userUuid } }
    );
  }

  getSelection(id: number): Observable<Selection> {
    return this.#http.get<Selection>(
      this.#baseRequestService.prepareUrl([this.#postUrl, 'selections', String(id)])
    );
  }

  getSelectionPosts(id: number, page = 1): Observable<ResponseList<CommunityItem>> {
    return this.#http.get<ResponseList<CommunityItem>>(
      this.#baseRequestService.prepareUrl([this.#postUrl, 'selections', String(id), 'posts']),
      { params: { page } },
    );
  }

  createSelection(data: Pick<Selection, 'name' | 'user_uuid'> & Partial<Pick<Selection, 'posts_uuids' | 'cover_id'>>): Observable<Selection> {
    return this.#http.post<Selection>(
      this.#baseRequestService.prepareUrl([this.#postUrl, 'selections']),
      data
    );
  }

  updateSelection(id: number, data: Partial<Selection>): Observable<Selection> {
    return this.#http.patch<Selection>(
      this.#baseRequestService.prepareUrl([this.#postUrl, 'selections', String(id)]),
      data
    );
  }

  deleteSelection(id: number): Observable<void> {
    return this.#http.delete<void>(
      this.#baseRequestService.prepareUrl([this.#postUrl, 'selections', String(id)])
    );
  }

  addPostToSelection(selectionId: number, postUuid: string, currentPostsUuids: string[]): Observable<Selection> {
    if (currentPostsUuids.includes(postUuid)) {
      return this.getSelection(selectionId);
    }
    return this.updateSelection(selectionId, {
      posts_uuids: [...currentPostsUuids, postUuid],
    });
  }

  removePostFromSelection(selectionId: number, postUuid: string, currentPostsUuids: string[]): Observable<Selection> {
    return this.updateSelection(selectionId, {
      posts_uuids: currentPostsUuids.filter(uuid => uuid !== postUuid),
    });
  }
}
